/**
 * Portal birthday celebration — frontend-only, independent of CRM gifts.
 * Shows once per calendar year when Contact.birthday matches today (local).
 */

const STORAGE_PREFIX = 'ewents.portal.birthdayCelebration.';

/** Parse YYYY-MM-DD (or ISO) to local month/day. */
export function birthdayMonthDay(
  birthday: string | null | undefined
): { month: number; day: number } | null {
  if (!birthday?.trim()) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(birthday.trim());
  if (!m) return null;
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { month, day };
}

export function isBirthdayToday(
  birthday: string | null | undefined,
  now: Date = new Date()
): boolean {
  const md = birthdayMonthDay(birthday);
  if (!md) return false;
  return now.getMonth() + 1 === md.month && now.getDate() === md.day;
}

export function celebrationStorageKey(userId: string, year: number): string {
  return `${STORAGE_PREFIX}${userId}.${year}`;
}

export function hasSeenBirthdayCelebration(
  userId: string,
  year: number = new Date().getFullYear(),
  storage: Pick<Storage, 'getItem'> = localStorage
): boolean {
  if (!userId) return true;
  return storage.getItem(celebrationStorageKey(userId, year)) === '1';
}

export function markBirthdayCelebrationSeen(
  userId: string,
  year: number = new Date().getFullYear(),
  storage: Pick<Storage, 'setItem'> = localStorage
): void {
  if (!userId) return;
  storage.setItem(celebrationStorageKey(userId, year), '1');
}

/** Whether the overlay should open for this session. */
export function shouldShowBirthdayCelebration(input: {
  userId: string | null | undefined;
  birthday: string | null | undefined;
  now?: Date;
  storage?: Pick<Storage, 'getItem'>;
}): boolean {
  const userId = input.userId?.trim();
  if (!userId) return false;
  const now = input.now ?? new Date();
  if (!isBirthdayToday(input.birthday, now)) return false;
  return !hasSeenBirthdayCelebration(userId, now.getFullYear(), input.storage ?? localStorage);
}

/** Random index into i18n `birthdayCelebration.messages` (stable for one overlay open). */
export function pickCelebrationMessageIndex(
  count: number,
  random: () => number = Math.random
): number {
  if (count <= 1) return 0;
  return Math.floor(random() * count);
}

/** Normalize ngx-translate array/object payload to non-empty strings. */
export function resolveCelebrationMessages(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map(m => String(m).trim()).filter(Boolean);
  }
  if (raw && typeof raw === 'object') {
    return Object.keys(raw as Record<string, unknown>)
      .sort((a, b) => Number(a) - Number(b))
      .map(k => String((raw as Record<string, unknown>)[k] ?? '').trim())
      .filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) {
    return [raw.trim()];
  }
  return [];
}
